import { DateFormat, DateUtility } from '@utility/date';
import { FlatButton } from '@elements/Button';
import { useMemo, useState } from 'react';
import {
  BorderTableCell,
  BorderTableRow,
  AppTableCell,
} from '@components/AppTable';
import { Badge } from '@skupreme/shared/ui';
import type { MouseEvent } from 'react';
import {
  CaretDownIcon,
  CaretUpIcon,
  ChatTextIcon,
  Check,
  CheckSquare,
  CircleIcon,
  ClipboardText,
  CopySimple,
  PencilSimpleIcon,
  StackIcon,
  Trash,
} from '@phosphor-icons/react';
import { DarkPopup } from '@elements/Popup';
import { CommonUtility } from '@utility/common';
import useUrlPermission from '@hooks/url-permission';
import PermissionTooltip from '@components/PermissionTooltip';
import { OrderStatus } from '@utility/constants/common';
import { ActionDropdown, ActionDropdownMenu } from '@elements/Dropdown';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { ChildBatchOrderList } from './ChildBatchOrderList';
import { IconSizes } from '@utility/constants/style-constants';
import { ProgressBar } from '@page-components/purchase-order';
import { TruckIcon } from '@phosphor-icons/react/dist/ssr';
import { OrderTime } from '@page-components/order';
import { FPInfoPopup } from '@page-components/order/ListInfoPopup';
import { ReadOnlyTip, useOrdersV2 } from '@features/orders/hooks/orders-v2';

/* The row toggles its orders; clicks on its own controls must not. */
const fromControl = (event: MouseEvent<HTMLElement>) =>
  !!(event.target as HTMLElement).closest('button, a, [role="dialog"]');

export function BatchRow({
  item,
  selectedOrder,
  toggleSelection,
  openPrintPopup,
  pickAndShipClick,
  deleteBatch,
  deleteOrder,
  status,
  fulfillBatch,
  editBatch,
  fetchRates,
  rateLoaders,
  orderRates,
  isShipped,
}) {
  const [expand, setExpand] = useState(false);
  const { disabled, hidden } = useUrlPermission(`/app/orders/list/${status}`);
  /* EN-4117: every batch action but viewing writes. */
  const { isV2 } = useOrdersV2();
  const [copied, setCopied] = useState(false);

  useDebouncedEffect(
    () => {
      if (expand) {
        fetchRates(item);
      }
    },
    [expand, item],
    100,
  );

  const deletedAllowed = useMemo(
    () => !item.orders.some(x => x.status === 'completed'),
    [item],
  );

  const itemsNotInPendingFulfillmentState = useMemo(() => {
    let total = 0;
    item?.items?.forEach(item => {
      if (item.state !== OrderStatus.PendingFulfillment) {
        total += 1;
      }
    });
    return total;
  }, [item]);

  const copyAllOrders = () => {
    CommonUtility.copyToClipboard(
      item.orders?.map(x => x.marketplace_order_display_id)?.join('\n'),
    );
    setCopied(true);
  };

  useDebouncedEffect(
    () => {
      if (copied) {
        setCopied(false);
      }
    },
    [copied],
    500,
  );

  const is100Percent = item.progress === 100;

  return (
    <>
      <BorderTableRow
        onClick={event => {
          if (!fromControl(event)) {
            setExpand(!expand);
          }
        }}
        className="action-hover sk:cursor-pointer sk:text-xs sk:[&>td]:overflow-visible!"
        verticalAlign="middle"
      >
        <BorderTableCell>
          <div className="border" />
          <div className="sk:flex sk:items-center sk:gap-2">
            <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-1">
              <StackIcon size={IconSizes.small} className="sk:shrink-0" />
              <span className="sk:truncate sk:font-bold">{item.name}</span>
            </div>
            {item.memo && (
              <DarkPopup
                trigger={<ChatTextIcon size={IconSizes.medium} />}
                position="top center"
              >
                <div className="sk:flex sk:flex-col">
                  <span className="sk:opacity-70">Order Memo</span>
                  <span>{item.memo}</span>
                </div>
              </DarkPopup>
            )}
          </div>
        </BorderTableCell>
        <AppTableCell>
          <OrderTime time={item.createdAt} isEnabled />
          <div className="sk:text-fg-tertiary">
            {DateUtility.formatDate(
              item.createdAt,
              DateFormat.meridianTimeDate,
            )}
          </div>
        </AppTableCell>
        <AppTableCell textAlign="center">
          <FPInfoPopup
            itemsNotInPendingFulfillmentState={
              itemsNotInPendingFulfillmentState
            }
            totalItems={item?.items?.length}
            isShipped={false}
            providers={[item.fulfillment_provider_id]}
            selectedFP={item.selectedFP}
            orderState={item.state}
          />
        </AppTableCell>
        <AppTableCell textAlign="left">
          {CommonUtility.numberWithCommas(item?.total_orders)}&nbsp;
          <span className="sk:text-fg-tertiary">
            ({CommonUtility.numberWithCommas(item?.total_items)} unit
            {item?.total_items > 1 ? 's' : ''})
          </span>
        </AppTableCell>
        <AppTableCell className="action-cell">
          <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
            {isShipped ? (
              <div />
            ) : (
              <div className="sk:w-[70%] sk:max-w-62.5">
                {!is100Percent ? (
                  <div className="sk:flex sk:items-center sk:gap-2">
                    <span className="sk:whitespace-nowrap">
                      {item.progress}%
                    </span>
                    <ProgressBar
                      progress={item.progress}
                      customColor="var(--sk-success-fg)"
                    />
                  </div>
                ) : (
                  <Badge tone="neutral">
                    <CircleIcon weight="fill" />
                    {item.status}
                  </Badge>
                )}
              </div>
            )}
            <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
              <ActionDropdown
                button
                className="border-action sk:min-w-[18px]"
                direction="left"
                icon="ellipsis horizontal"
                simple
              >
                <ActionDropdownMenu>
                  {item.pick_list_url && (
                    <PermissionTooltip
                      disabled={disabled}
                      hidden={hidden}
                      wrapperClassName={
                        disabled
                          ? 'sk:cursor-not-allowed sk:opacity-70 sk:[&_*]:cursor-not-allowed'
                          : ''
                      }
                    >
                      <FlatButton
                        onClick={() => openPrintPopup(item.pick_list_url)}
                        disabled={disabled}
                        className="dropdown-action"
                      >
                        <ClipboardText />
                        View Pick List
                      </FlatButton>
                    </PermissionTooltip>
                  )}
                  {CommonUtility.isFunction(editBatch) && (
                    <ReadOnlyTip>
                      <FlatButton
                        onClick={() => editBatch(item)}
                        disabled={isV2}
                        className="dropdown-action"
                      >
                        <PencilSimpleIcon /> Edit
                      </FlatButton>
                    </ReadOnlyTip>
                  )}
                  {!isShipped && (
                    <>
                      {is100Percent && (
                        <PermissionTooltip
                          disabled={disabled}
                          hidden={hidden}
                          position="left center"
                        >
                          <ReadOnlyTip>
                            <FlatButton
                              onClick={() => pickAndShipClick(item)}
                              disabled={disabled || isV2}
                              className="dropdown-action"
                            >
                              <TruckIcon />
                              Pick & Ship
                            </FlatButton>
                          </ReadOnlyTip>
                        </PermissionTooltip>
                      )}
                      <ReadOnlyTip>
                        <FlatButton
                          onClick={() => fulfillBatch(item)}
                          disabled={isV2}
                          className="dropdown-action"
                        >
                          <CheckSquare /> Fulfill
                        </FlatButton>
                      </ReadOnlyTip>
                    </>
                  )}
                  <FlatButton
                    onClick={copyAllOrders}
                    className="dropdown-action"
                  >
                    {copied ? (
                      <>
                        <Check size={20} />
                        &nbsp;Copied
                      </>
                    ) : (
                      <>
                        <CopySimple size={20} /> Copy Order IDs
                      </>
                    )}
                  </FlatButton>
                  {deletedAllowed && !item.completed && (
                    <PermissionTooltip
                      disabled={disabled}
                      hidden={hidden}
                      position="top right"
                      wrapperClassName="sk:p-0!"
                    >
                      <ReadOnlyTip>
                        <FlatButton
                          onClick={() => deleteBatch(item)}
                          disabled={isV2}
                          className="dropdown-action dropdown-danger-action"
                        >
                          <Trash /> Delete
                        </FlatButton>
                      </ReadOnlyTip>
                    </PermissionTooltip>
                  )}
                </ActionDropdownMenu>
              </ActionDropdown>
              {expand ? (
                <CaretUpIcon size={IconSizes.medium} />
              ) : (
                <CaretDownIcon size={IconSizes.medium} />
              )}
            </div>
          </div>
        </AppTableCell>
      </BorderTableRow>
      {expand && (
        <ChildBatchOrderList
          orders={item?.orders}
          selectedOrder={selectedOrder}
          toggleSelection={toggleSelection}
          batchId={item._id}
          deleteOrder={deleteOrder}
          rateLoaders={rateLoaders}
          orderRates={orderRates}
          fulfillment_provider_id={item.fulfillment_provider_id?._id}
          isShipped={isShipped}
        />
      )}
    </>
  );
}
